/**
 * Map internal / service errors to clear messages suitable for end users.
 * Keep technical details in console logs only.
 */

const RULES = [
  {
    test: /permission-denied|PERMISSION_DENIED|Missing or insufficient permissions/i,
    message: 'You do not have permission to perform this action.',
  },
  {
    test: /keyshares|dkRequestSuccess|decryption_keys|decryption_key|chain_response|encrypted_keyshare|_byteString|ciphertext found in this entry/i,
    message:
      'The decryption key for this transfer is not available yet. Try again in a moment, or request a new key.',
  },
  {
    test: /Cannot aggregate keyshares|encrypted_keyshare_index|Lagrange|VSS/i,
    message: 'The decryption key could not be assembled. Please try again or contact an administrator.',
  },
  {
    test: /No kv entries found|Failed to query kv|Failed to get and decrypt kv/i,
    message: 'No decryption key was found for this transfer. Request a key and try again.',
  },
  {
    test: /Entry \d+ missing|Document keys:|Value keys:/i,
    message: 'The decryption key data is incomplete. Please request a new key and try again.',
  },
  {
    test: /Session private key not found|wrong password|Incomplete encrypted key/i,
    message: 'Your session key is missing. Please log out and log in again.',
  },
  {
    test: /Polling aborted/i,
    message: 'The key request was cancelled.',
  },
  {
    test: /Timeout:.*[Dd]ecryption key not found|not found after \d+ seconds/i,
    message: 'Timed out waiting for the decryption key. Please try again.',
  },
  {
    test: /Timed out fetching transaction from|Failed to fetch transaction from .+ RPC|REACT_APP_STABLE_RPC_URL/i,
    message:
      'Could not reach the chain RPC to load this transfer. Please try again later.',
  },
  {
    test: /Network error|Failed to fetch|unavailable|INTERNAL ASSERTION|firestore\/unavailable/i,
    message: 'Network error. Check your connection and try again.',
  },
  {
    test: /Security violation|unauthorized addresses/i,
    message: 'That account is outside your audit scope.',
  },
  {
    test: /WASM|decryption module/i,
    message: 'The decryption module is still loading. Please wait a moment and try again.',
  },
  {
    test: /IBE private key is required/i,
    message: 'A decryption key is required before the amount can be revealed.',
  },
  {
    test: /Auditor public key not found|Auditor UID not found/i,
    message: 'Your auditor profile is incomplete. Please contact an administrator.',
  },
  {
    test: /User address is required|Transaction user address not found/i,
    message: 'This transfer is missing account information and cannot be decrypted.',
  },
  {
    test: /Backend response missing|status is not success|Failed to request decryption key/i,
    message: 'The compliance server could not issue a decryption key. Please try again.',
  },
  {
    test: /Unable to connect to (backend|compliance)/i,
    message: 'Could not reach the compliance server. Check your connection and try again.',
  },
  {
    test: /invalid mac hash|Invalid ciphertext|Invalid private key|btcec|unsupported curve/i,
    message: 'Decryption failed. Your session key may not match this record. Try logging out and back in.',
  },
  {
    test: /Failed to decrypt data|Failed to decrypt byteString|Failed to decrypt key/i,
    message: 'Could not decrypt the key. Please log out, log in again, and retry.',
  },
  {
    test: /firestore|FirebaseError|collection|document|snapshot/i,
    message: 'Something went wrong while loading data. Please try again.',
  },
];

const stripPrefixes = [
  /^Key request failed:\s*/i,
  /^Failed to retrieve decryption key:\s*/i,
  /^Decryption failed:\s*/i,
  /^Failed to load user addresses:\s*/i,
  /^Failed to load transactions:\s*/i,
  /^Failed to get and decrypt kv entries:\s*/i,
];

export function toUserFriendlyError(error, fallback = 'Something went wrong. Please try again.') {
  const raw =
    typeof error === 'string'
      ? error
      : error?.message || error?.details?.message || '';

  if (!raw || typeof raw !== 'string') return fallback;

  let message = raw.trim();
  for (const prefix of stripPrefixes) {
    message = message.replace(prefix, '');
  }

  for (const rule of RULES) {
    if (rule.test.test(raw) || rule.test.test(message)) {
      return rule.message;
    }
  }

  // Hide leftover internals (snake_case field names, code-like tokens, paths)
  if (
    /[_/]{1,}[a-z]+_[a-z]+/i.test(message) ||
    /\b(kv|firestore|firebase|collection|documentId|byteString)\b/i.test(message) ||
    message.length > 180
  ) {
    return fallback;
  }

  return message || fallback;
}
